import { Flex, OverflowText, Icon, Text, PillWrapper, Pill } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { SanitizeFieldValue, IsFieldNotConfigured } from "../../RecordInfoCard/utils";
import { PositionFieldConfig } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/ProfileConfiguration/ProfileConfiguration.utils";

type Props = {
  config: PositionFieldConfig;
  header1Text: string;
  showHeader1: boolean;

  header2Text?: string;
  showHeader2: boolean;

  subheader1Text: string;
  showSubheader1: boolean;
  record: Record<string, any>;

  showAssignees?: boolean;
  assignees?: string;

  reportsToLabel?: string;
};

const scrollOptions = { behavior: "smooth", block: "nearest", container: "nearest" } as any;

const ReportsToPill = ({ label }: { label: string }) => {
  return (
    <PillWrapper
      style={{
        height: "100%",
        width: "100%",
        maxWidth: "75%",
        display: "flex",
        alignItems: "center"
      }}
    >
      <Pill label={label} />
    </PillWrapper>
  );
};

export const PositionHeadingProfileCard = (props: Props) => {
  const {
    config,
    header1Text,
    showHeader1,
    subheader1Text,
    showSubheader1,
    header2Text,
    showHeader2,

    assignees,
    showAssignees = false,
    reportsToLabel,

    record
  } = props;

  const getPositionDateDisplay = () => {
    const [startDateConfig, endDateConfig] = [config.positionStartDate, config.positionEndDate];
    const [startDateValue, endDateValue] = [
      startDateConfig ? record?.[startDateConfig] : appstr("viewernotset"),
      endDateConfig ? record?.[endDateConfig] : appstr("viewernotset")
    ];

    return `${startDateValue || appstr("Blank")} - ${endDateValue || appstr("Blank")}`;
  };

  const baseClass = "oc-profile-heading";
  const header1NotConfigured = IsFieldNotConfigured(header1Text, null);
  const subheader1NotConfigured = IsFieldNotConfigured(subheader1Text, null);
  const header2NotConfigured = IsFieldNotConfigured(header2Text, null);
  const workLocationNotConfigured = IsFieldNotConfigured(record[config.workLocation], null);

  return (
    <Flex direction="column" gap="16px" className={baseClass}>
      <Flex direction="column" flex="1" className={`${baseClass}-basic-fields`}>
        <Flex direction="row" justifyContent="flex-start" gap="8px" alignItems="flex-end">
          {showHeader1 && (
            <OverflowText
              size="large"
              className="oc-font-weight-500"
              dataTestId={`${baseClass}-header1`}
              colorModifier={header1NotConfigured ? "faintgrey" : undefined}
              modifier={header1NotConfigured ? "italic" : undefined}
            >
              {SanitizeFieldValue(header1Text, null, appstr("Blank"))}
            </OverflowText>
          )}
          {showSubheader1 && (
            <Text
              size="xsmall"
              colorModifier={subheader1NotConfigured ? "faintgrey" : "grey"}
              modifier={subheader1NotConfigured ? "italic" : undefined}
              data-testid={`${baseClass}-subheader1`}
            >
              {SanitizeFieldValue(subheader1Text, null, appstr("Blank"))}
            </Text>
          )}
        </Flex>
        {showHeader2 && (
          <OverflowText
            size="small"
            colorModifier={header2NotConfigured ? "faintgrey" : "grey"}
            modifier={header2NotConfigured ? "italic" : undefined}
            dataTestId={`${baseClass}-header2`}
          >
            {SanitizeFieldValue(header2Text, null, appstr("Blank"))}
          </OverflowText>
        )}
      </Flex>

      <Flex direction="column" gap="8px" className={`${baseClass}-additional-info`}>
        {config.workLocation && (
          <Text
            size="small"
            colorModifier={workLocationNotConfigured ? "faintgrey" : undefined}
            modifier={workLocationNotConfigured ? "italic" : undefined}
          >
            <Icon name="location-dot" size="small" />
            {SanitizeFieldValue(record[config.workLocation], null, "--")}
          </Text>
        )}
        {(config.positionStartDate || config.positionEndDate) && (
          <Text size="small">
            <Icon name="calendar" size="small" />
            {getPositionDateDisplay()}
          </Text>
        )}
        {showAssignees && (
          <Text size="small">
            <Icon name="user" size="small" />
            <a
              href="#"
              onClick={() =>
                document
                  .getElementById("ocn-profile-card-holders-section")
                  ?.scrollIntoView(scrollOptions)
              }
              className={`${baseClass}-assignees-link`}
            >
              {assignees == "1" ? appstr("1 assignee") : appstr("{n} assignees", { n: assignees })}
            </a>
          </Text>
        )}
      </Flex>

      <Flex direction="row" alignItems="center" justifyContent="flex-start" gap="8px">
        <Text size="small" className="oc-font-weight-500">
          {appstr("Reports to")}:
        </Text>
        {reportsToLabel ? <ReportsToPill label={reportsToLabel} /> : <Text size="small">--</Text>}
      </Flex>
    </Flex>
  );
};
